import { SHStack, SHTypography } from "@components/design-systems";
import { MAAnalysisDataDTO } from "@models/managed-accounts/entities/step/fee";
import { ManagedAccountFieldType } from "@models/managed-accounts/enums/fieldType";
import { ManagedAccountFieldTypeValue } from "@models/managed-accounts/enums/fieldTypeValue";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { useTheme } from "@mui/material";
import { MultipleInformationButton } from "@pages/managed-accounts/_id/steps/fee-analysis/components/buttons/multiple-information";
import { isEmpty } from "lodash";
import { memo } from "react";
import { RenderSubProductCellByTypeComponentMap } from "../../../../configs";
import { NotDisclosedCell } from "../../value-renders/not-disclosed-cell";

type Props = {
  analysisData: MAAnalysisDataDTO;
  variableName?: AnalysisVariable;
};

export const FeatureSubProductCell = memo(
  ({ analysisData, variableName }: Props) => {
    const { palette } = useTheme();

    const renderValueByType = () => {
      const { fieldTypeId } = analysisData || {};
      const parsedTypeId = fieldTypeId as ManagedAccountFieldType;
      const RenderByTypeComponent =
        RenderSubProductCellByTypeComponentMap[parsedTypeId];

      if (RenderByTypeComponent)
        return (
          <RenderByTypeComponent
            analysisData={analysisData}
            variableName={variableName}
          />
        );

      return (
        <SHStack alignItems={"center"} justifyContent={"center"}>
          <SHTypography
            variant="body3"
            zIndex="1"
            colorVariant="third"
            textAlign="justify"
            border={"1px solid red"}
          >
            {parsedTypeId}
          </SHTypography>
        </SHStack>
      );
    };

    const renderFeatureValue = () => {
      if (analysisData?.value === ManagedAccountFieldTypeValue.NotDisclosed) {
        return <NotDisclosedCell analysisData={analysisData} />;
      }

      return renderValueByType();
    };

    const renderAnalysisDataValueTooltip = (
      analysisData: MAAnalysisDataDTO,
    ) => {
      const invalidData = [
        !analysisData,
        !analysisData?.tooltip && !analysisData?.secondaryTooltip,
        isEmpty(analysisData?.tooltip) &&
          isEmpty(analysisData?.secondaryTooltip),
      ].some(Boolean);

      if (invalidData) {
        return null;
      }

      const { additionalText, header, data } = analysisData?.tooltip || {};
      const { header: secondaryTitle, data: secondaryData } =
        analysisData?.secondaryTooltip || {};

      if (additionalText && analysisData?.tooltip?.additionalText) {
        return (
          <MultipleInformationButton
            additionalText={analysisData.tooltip.additionalText}
            secondaryTitle={secondaryTitle}
            secondaryData={secondaryData}
            secondaryContentColor={palette.error[500]}
          />
        );
      }

      return (
        <MultipleInformationButton
          title={header}
          data={data}
          secondaryTitle={secondaryTitle}
          secondaryData={secondaryData}
          secondaryContentColor={palette.error[500]}
        />
      );
    };

    return (
      <SHStack
        width={"100%"}
        alignItems={"center"}
        justifyItems={"center"}
        direction={"row"}
      >
        <SHStack
          direction={"row"}
          width={"100%"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={1}
        >
          {renderFeatureValue()}
          {renderAnalysisDataValueTooltip(analysisData)}
        </SHStack>
      </SHStack>
    );
  },
);
